import { keyBy } from 'lodash';
import { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useDeleteModal } from '~/eds/src/components/hooks/useDeleteModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { useDebouncedValue } from '~/eds/src/hooks/useDebouncedValue';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import { TableState } from '~/eds/src/types/Table';
import { GroupFormModal } from '~/features/groups/GroupFormModal';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCanEditEntityGroups } from '~/hooks/useCanEditEntityGroups';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { Group } from '~/redux/api/methods/groups/types';
import { UpdateGroupParams } from '~/redux/api/methods/groups/updateGroup';

import { messages } from './[groupId]';
import { NewGroupButton } from './NewGroupButton';

const pageMessages = defineMessages({
  pageTitle: {
    id: 'WorkflowTicketsGroups.pageTitle',
    defaultMessage: 'Groups',
    description: 'Page title for workflow groups',
  },
  nameColumn: {
    id: 'WorkflowTicketsGroups.nameColumn',
    defaultMessage: 'Group',
  },
  createdByColumn: {
    id: 'WorkflowTicketsGroups.createdByColumn',
    defaultMessage: 'Created By',
  },
  createdDateColumn: {
    id: 'WorkflowTicketsGroups.createdDateColumn',
    defaultMessage: 'Created Date',
  },
  lastUpdatedColumn: {
    id: 'WorkflowTicketsGroups.lastUpdatedColumn',
    defaultMessage: 'Last Updated',
  },
  numberOfItemsColumn: {
    id: 'WorkflowTicketsGroups.numberOfItemsColumn',
    defaultMessage: '# of Items',
  },
  notesColumn: {
    id: 'WorkflowTicketsGroups.notesColumn',
    defaultMessage: 'Notes',
  },
});

interface Props {
  basePath?: string;
}

const PAGE_SIZE = 20;

const WorkflowGroupsPage = ({ basePath = '/workflow/groups' }: Props) => {
  const { formatMessage } = useIntl();
  const hasGroupedTicketsAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const currentUser = useCurrentUser();
  const [tableState, setTableState] = useState<Partial<TableState>>(() => ({
    pageIndex: 1,
    pageSize: PAGE_SIZE,
  }));
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText);

  const { data, isFetching } = api.endpoints.getGroups.useQuery({
    offset:
      ((tableState?.pageIndex ?? 1) - 1) * (tableState?.pageSize ?? PAGE_SIZE),
    limit: tableState?.pageSize ?? PAGE_SIZE,
    ...(hasGroupedTicketsAndDocs && debouncedSearch
      ? { search: debouncedSearch }
      : {}),
  });

  const [updateGroup, { isLoading: isUpdatingGroup }] =
    api.endpoints.updateGroup.useMutation();
  const [deleteGroup, { isLoading: isDeletingGroup }] =
    api.endpoints.deleteGroup.useMutation();

  const isLoading = isFetching;

  const { data: users } = api.endpoints.getAllUsers.useQuery(
    currentUser?.client,
  );
  const usersLookup = useMemo(() => keyBy(users, 'id'), [users]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: formatMessage(pageMessages.nameColumn),
        cellType: 'link',
        mapCellProps: (data: Group) => ({
          text: data.attributes.name,
          pathname: `${basePath}/${data.id}`,
        }),
      },
      {
        key: 'createdBy',
        title: formatMessage(pageMessages.createdByColumn),
        cellType: 'user',
        mapCellProps: (data: Group) => {
          const user = usersLookup[data.attributes.createdBy ?? ''];

          return {
            user,
            mode: 'avatar-name',
          };
        },
      },
      {
        key: 'dateAdded',
        title: formatMessage(pageMessages.createdDateColumn),
        cellType: 'datetime',
        mapCellProps: (data: Group) => ({
          datetime: data.attributes.createdAt,
        }),
      },
      {
        key: 'lastModified',
        title: formatMessage(pageMessages.lastUpdatedColumn),
        cellType: 'datetime',
        mapCellProps: (data: Group) => ({
          datetime: data.attributes.updatedAt,
        }),
      },
      {
        key: 'numberOfDocs',
        title: formatMessage(pageMessages.numberOfItemsColumn),
        cellType: 'number',
        mapCellProps: (data: Group) => ({
          number: data.attributes.nodesCount,
        }),
      },
      {
        key: 'note',
        title: formatMessage(pageMessages.notesColumn),
        cellType: 'text',
        mapCellProps: (data: Group) => ({
          text: data.attributes.note || '',
        }),
      },
    ],
    [formatMessage, basePath, usersLookup],
  );

  const handleUpdateGroup = async ({ id, ...payload }: UpdateGroupParams) => {
    const group = data?.data.find((group) => group.id === id);

    await updateGroup({
      id,
      ...payload,
      rtkQueryOptions: {
        showSuccessToast: formatMessage(messages.groupUpdated, {
          groupName: group?.attributes.name,
        }),
      },
    });
    setEditingGroup(null);
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroupId) return;
    await deleteGroup({
      payload: deletingGroupId,
      rtkQueryOptions: {
        showSuccessToast: formatMessage(messages.groupDeleted),
      },
    });
    hideDeleteGroupModal();
    setDeletingGroupId(null);
  };

  const [deleteGroupConfirmModal, showDeleteGroupModal, hideDeleteGroupModal] =
    useDeleteModal({
      title: formatMessage(messages.deleteGroupConfirmTitle),
      children: (
        <Text>{formatMessage(messages.deleteGroupConfirmMessage)}</Text>
      ),
      isDeleting: isDeletingGroup,
      onDelete: handleDeleteGroup,
    });

  const hasEditPermission = useCanEditEntityGroups();

  const rowActions = useMemo(
    () =>
      hasEditPermission.hasPermission
        ? [
            {
              label: formatMessage(messages.editGroup),
              icon: 'edit' as const,
              onClick: (group: Group) => {
                setEditingGroup(group);
              },
            },
            {
              label: formatMessage(messages.deleteGroup),
              icon: 'trash' as const,
              onClick: (group: Group) => {
                setDeletingGroupId(group.id);
                showDeleteGroupModal();
              },
            },
          ]
        : [],
    [formatMessage, showDeleteGroupModal, hasEditPermission.hasPermission],
  );

  return (
    <PageLayout
      title={formatMessage(pageMessages.pageTitle)}
      headerCalloutContent={<NewGroupButton />}
    >
      {hasGroupedTicketsAndDocs ? (
        <Layout direction="column" spacing={4}>
          <SearchInput
            name="group-search"
            value={searchText}
            onChange={(value) => {
              setSearchText(value ?? '');
              setTableState((state) => ({ ...state, pageIndex: 1 }));
            }}
            placeholder={formatMessage({
              id: 'WorkflowTicketsGroups.searchPlaceholder',
              defaultMessage: 'Search groups',
              description: 'Placeholder for Search input',
            })}
          />
          <Table
            isLoading={isLoading}
            data={data?.data ?? []}
            columns={columns}
            name="workflow-groups"
            state={tableState}
            totalCount={data?.meta?.total ?? 0}
            rowActions={rowActions}
            onPaginate={({ pageIndex }: { pageIndex: number }) => {
              setTableState((state) => ({ ...state, pageIndex }));
            }}
            onPageSizeChange={(pageSize: number) => {
              setTableState((state) => ({ ...state, pageSize, pageIndex: 1 }));
            }}
          />
        </Layout>
      ) : (
        <Table
          isLoading={isLoading}
          data={data?.data ?? []}
          columns={columns}
          name="workflow-groups"
          state={tableState}
          totalCount={data?.meta?.total ?? 0}
          rowActions={rowActions}
          onPaginate={({ pageIndex }: { pageIndex: number }) => {
            setTableState((state) => ({ ...state, pageIndex }));
          }}
          onPageSizeChange={(pageSize: number) => {
            setTableState((state) => ({ ...state, pageSize }));
          }}
        />
      )}

      {deleteGroupConfirmModal}

      {editingGroup && (
        <GroupFormModal
          isVisible={!!editingGroup}
          onHide={() => setEditingGroup(null)}
          onSubmit={(data) =>
            handleUpdateGroup({ id: editingGroup.id, ...data })
          }
          title={formatMessage(messages.editGroup)}
          submitButtonText={formatMessage(messages.saveGroup)}
          initialName={editingGroup.attributes.name}
          initialNote={editingGroup.attributes.note}
          isLoading={isUpdatingGroup}
        />
      )}
    </PageLayout>
  );
};

export default WorkflowGroupsPage;

export const DocumentGroupsNewPage = () => (
  <WorkflowGroupsPage basePath="/documents/groups" />
);
